import React from 'react';
import { cva } from 'class-variance-authority';
import TextareaAutosize from 'react-textarea-autosize';
import { icons } from '../icons';
import { inputFrameClasses } from './KoobInputText';

type KoobPromptInputProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className' | 'style' | 'rows' | 'value' | 'onChange' | 'onSubmit'
> & {
  value: string;
  onValueChange: (value: string) => void;
  /** Enter (without Shift) or the send button; not called while empty or disabled. */
  onSubmit: (value: string) => void;
  /** Accessible name of the send button. */
  sendLabel: string;
  /** Small text left of the send button, e.g. how many questions are left. */
  hint?: React.ReactNode;
  maxRows?: number;
  /** sm: one line with the send button beside the text, for tight spaces. */
  size?: 'md' | 'sm';
};

// The UI kit's composer: a navy border and a pale navy ring on focus, the
// send button a navy square, pale while there is nothing to send.
const frameVariants = cva(
  `flex flex-col transition-[border-color,box-shadow] focus-within:border-k-h focus-within:ring-4 focus-within:ring-navy-100 has-[textarea:disabled]:bg-navy-50 ${inputFrameClasses}`,
  {
    variants: {
      size: {
        md: 'gap-1 rounded-[14px] py-2 pl-3.5 pr-2',
        sm: 'rounded-xl py-1 pl-3 pr-1',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

const sendVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center border-none bg-k-h text-white transition-colors hover:bg-navy-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-k-h focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-navy-100 disabled:text-navy-400',
  {
    variants: {
      size: { md: 'size-9 rounded-[10px]', sm: 'size-7 rounded-lg text-xs' },
    },
    defaultVariants: { size: 'md' },
  },
);

/**
 * Chat composer, as in AI chat apps: a textarea that grows with its text and
 * a send button beside it, in the same frame. Enter sends, Shift+Enter adds a line.
 */
const KoobPromptInput = React.forwardRef<
  HTMLTextAreaElement,
  KoobPromptInputProps
>(
  (
    {
      value,
      onValueChange,
      onSubmit,
      sendLabel,
      hint,
      disabled,
      maxRows = 6,
      size = 'md',
      ...props
    },
    ref,
  ) => {
    const canSend = !disabled && value.trim() !== '';
    const send = () => canSend && onSubmit(value);
    const sendButton = (
      <button
        type="button"
        onClick={send}
        disabled={!canSend}
        aria-label={sendLabel}
        title={sendLabel}
        className={sendVariants({ size })}
      >
        <i className={icons.arrowUp} aria-hidden="true" />
      </button>
    );

    // @tailwindcss/forms rings any focused textarea, so the textarea turns
    // that ring off: the frame shows focus.
    return (
      <div className={frameVariants({ size })}>
        <div className="flex items-end gap-2">
          <TextareaAutosize
            ref={ref}
            value={value}
            onChange={(event) => onValueChange(event.target.value)}
            onKeyDown={(event) => {
              if (
                event.key === 'Enter' &&
                !event.shiftKey &&
                !event.nativeEvent.isComposing
              ) {
                event.preventDefault();
                send();
              }
            }}
            disabled={disabled}
            minRows={1}
            maxRows={maxRows}
            className={`block w-full resize-none self-center border-none bg-transparent p-0 text-sm leading-[22px] text-k-h shadow-none outline-none placeholder:text-navy-400 focus:shadow-none focus:outline-none focus:ring-0 disabled:cursor-not-allowed ${size === 'sm' ? 'py-1' : 'py-1.5'}`}
            {...props}
          />
          {sendButton}
        </div>
        {/* ponytail: no hint line in sm; add one when a tight space needs it */}
        {size === 'md' && hint && (
          <span className="text-xs text-k-p">{hint}</span>
        )}
      </div>
    );
  },
);

KoobPromptInput.displayName = 'KoobPromptInput';

export default KoobPromptInput;
